import { useI18n } from "@core/i18n/i18n";
import { HealthcareTeamMember, isRegisteredHealthcareTeamMember } from "@domain/healthcare-team/healthcare-team";
import { Patient } from "@domain/patient/patient";
import { User } from "@domain/user/user";
import styled from "@emotion/styled";
import { HealthcareTeamMemberActionButton } from "@ui/pages/patient-details/components/healthcare-team-tab/components/healthcare-team-member-action-button";
import { HealthcareTeamMemberTag } from "@ui/pages/patient-details/components/healthcare-team-tab/components/healthcare-team-member-tag";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { formatName } from "@utils/string-utils";
import React from "react";

interface HealthcareTeamMemberRowProps {
	patient: Patient;
	member: HealthcareTeamMember;
	user: User;
}

export const HealthcareTeamMemberRow = ({ member, user, patient }: HealthcareTeamMemberRowProps): JSX.Element => {
	const { format } = useI18n();
	const name = formatName(member, member.email);

	return (
		<Row key={member.id}>
			<LeftContainer>
				<FullNameContainer>
					<FullNameView>{name}</FullNameView>
					{isRegisteredHealthcareTeamMember(member) && member.ptId === user.id && (
						<YouView>{format("patientDetails.healthcareTeam.you")}</YouView>
					)}
				</FullNameContainer>
				{name && <EmailView>{member.email}</EmailView>}
			</LeftContainer>
			<RightContainer hasSingleChild={user.id === member.ptId}>
				<HealthcareTeamMemberTag member={member} />
				<HealthcareTeamMemberActionButton user={user} patient={patient} healthcareTeamMember={member} />
			</RightContainer>
		</Row>
	);
};

const Row = styled.div`
	${row(undefined, "spacebetween", "center")};
	padding: 24px;
	border-bottom: 1px solid ${colors.grey25};

	@media ${physioMeMobileBreakpoint} {
		${stack(8)};
	}
`;

const LeftContainer = styled.div`
	${stack(undefined, "center", "flexstart")};
`;

const RightContainer = styled.div<{ hasSingleChild: boolean }>`
	${row(32, "flexend", "center")};
	@media ${physioMeMobileBreakpoint} {
		${row(8, "spacebetween", "center")};
		width: 100%;
		justify-content: ${({ hasSingleChild }) => (hasSingleChild ? "flex-end" : "flex-between")};
	}
`;

const FullNameContainer = styled.div`
	${row(4, "flexstart", "center")};
	flex-wrap: wrap;
`;

const FullNameView = styled.span`
	color: ${colors.black};
	font-size: 16px;
	font-weight: 700;
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
	@media ${physioMeTabletBreakpoint} {
		max-width: 80vw;
	}
`;

const YouView = styled.div`
	color: ${colors.grey};
	font-size: 16px;
	font-weight: 100;
`;

const EmailView = styled.div`
	color: ${colors.grey};
	font-size: 12px;
	font-weight: 600;
`;
